import React, { useMemo, useRef, useState } from 'react';
import {
  useListComplianceBulletinsQuery,
  useUploadComplianceBulletinMutation,
  useUpdateComplianceBulletinMutation,
  useDeleteComplianceBulletinMutation,
} from '../app/api';
import { useIsMobile } from './utils';

const brand = {
  deepBlue:  '#0a2947',
  tealBlue:  '#0069a6',
  goldenrod: '#d09b2c',
  charcoal:  '#58585b',
  softGray:  '#e9e6e1',
  green:     '#1a7f4e',
  red:       '#c0392b',
};

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
                'July', 'August', 'September', 'October', 'November', 'December'];

/**
 * Compliance Quick Hitter admin page.
 *
 * Compliance author each issue outside the app (HTML or PDF) and upload it here.
 * The newest PUBLISHED issue is what the hub's Quick Links tile opens, so an
 * issue can be uploaded unpublished, previewed in the same viewer employees will
 * see, then published when it's right.
 */
export default function ComplianceQuickHitter() {
  const isMobile = useIsMobile();
  const { data: bulletins = [], isLoading, refetch } = useListComplianceBulletinsQuery();
  const [uploadBulletin, { isLoading: uploading }] = useUploadComplianceBulletinMutation();
  const [updateBulletin] = useUpdateComplianceBulletinMutation();
  const [deleteBulletin] = useDeleteComplianceBulletinMutation();

  const fileRef = useRef(null);
  const today = new Date();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState('Compliance Quick Hitter');
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [published, setPublished] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);

  const periodLabel = `${MONTHS[month]} ${year}`;
  // First of the chosen month — orders the archive and picks the current issue,
  // so a bulletin uploaded late still files under the month it covers.
  const sortDate = `${year}-${String(month + 1).padStart(2, '0')}-01`;

  const currentId = useMemo(() => {
    const live = bulletins.filter(b => b.published);
    return live.length ? live[0].id : null;
  }, [bulletins]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!file) { setError('Choose an HTML or PDF file to upload.'); return; }
    const form = new FormData();
    form.append('file', file);
    form.append('title', title.trim() || 'Compliance Quick Hitter');
    form.append('sort_date', sortDate);
    form.append('period_label', periodLabel);
    form.append('published', published ? 'true' : 'false');
    try {
      await uploadBulletin({ body: form }).unwrap();
      setFile(null);
      if (fileRef.current) fileRef.current.value = '';
      setNotice(`${periodLabel} uploaded${published ? ' and published' : ' as a draft'}.`);
      // Tags are inert in this API slice, so refetch explicitly.
      refetch();
    } catch (err) {
      setError(err?.data?.detail || 'Upload failed.');
    }
  };

  const togglePublished = async (b) => {
    setError(null);
    try {
      await updateBulletin({ id: b.id, body: { published: !b.published } }).unwrap();
      refetch();
    } catch (err) {
      setError(err?.data?.detail || 'Could not update that issue.');
    }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Delete "${b.title}" (${b.period_label || b.sort_date})? `
                        + 'This removes the file as well and cannot be undone.')) return;
    setError(null);
    try {
      await deleteBulletin({ id: b.id }).unwrap();
      setNotice('Issue deleted.');
      refetch();
    } catch (err) {
      setError(err?.data?.detail || 'Could not delete that issue.');
    }
  };

  const inputStyle = {
    width: '100%', border: '1px solid rgba(10,41,71,0.2)', borderRadius: 6,
    padding: '0.4rem 0.6rem', fontSize: '0.88rem',
  };
  const labelStyle = {
    fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase',
    letterSpacing: '0.5px', color: brand.charcoal, marginBottom: '0.25rem',
    display: 'block',
  };

  return (
    <div style={{ background: brand.softGray, minHeight: '100vh', padding: isMobile ? '0.75rem' : '1.5rem' }}>
      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden', marginBottom: '1.25rem' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="bi bi-clipboard2-check-fill" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            Upload an Issue
          </h5>
        </div>
        <form onSubmit={onSubmit} style={{ padding: isMobile ? '0.75rem' : '1.25rem' }}>
          <p style={{ color: brand.charcoal, fontSize: '0.84rem', marginTop: 0 }}>
            Upload the issue as a self-contained <strong>HTML</strong> or <strong>PDF</strong> file
            (10&nbsp;MB max). Employees open it from the <strong>Quick Links</strong> tile on the hub;
            whichever published issue is newest is the one they see.
          </p>
          <div className="row g-3">
            <div className="col-12 col-md-6">
              <label style={labelStyle} htmlFor="cqh-file">File</label>
              <input
                id="cqh-file"
                ref={fileRef}
                type="file"
                accept=".html,.htm,.pdf"
                onChange={e => setFile(e.target.files?.[0] || null)}
                style={inputStyle}
              />
            </div>
            <div className="col-12 col-md-6">
              <label style={labelStyle} htmlFor="cqh-title">Title</label>
              <input
                id="cqh-title"
                type="text"
                value={title}
                onChange={e => setTitle(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div className="col-6 col-md-3">
              <label style={labelStyle} htmlFor="cqh-month">Month</label>
              <select id="cqh-month" value={month} onChange={e => setMonth(Number(e.target.value))} style={inputStyle}>
                {MONTHS.map((m, i) => <option key={m} value={i}>{m}</option>)}
              </select>
            </div>
            <div className="col-6 col-md-3">
              <label style={labelStyle} htmlFor="cqh-year">Year</label>
              <input
                id="cqh-year"
                type="number"
                value={year}
                onChange={e => setYear(Number(e.target.value))}
                style={inputStyle}
              />
            </div>
            <div className="col-12 col-md-3 d-flex align-items-end">
              <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontSize: '0.85rem', color: brand.deepBlue, marginBottom: '0.35rem' }}>
                <input type="checkbox" checked={published} onChange={e => setPublished(e.target.checked)} />
                Publish immediately
              </label>
            </div>
            <div className="col-12 col-md-3 d-flex align-items-end">
              <button
                type="submit"
                disabled={uploading}
                style={{
                  background: brand.tealBlue, color: 'white', border: 'none',
                  borderRadius: 6, padding: '0.45rem 1.1rem', fontWeight: 600,
                  fontSize: '0.88rem', marginBottom: '0.25rem', width: '100%',
                  cursor: uploading ? 'wait' : 'pointer',
                }}
              >
                {uploading ? 'Uploading…' : 'Upload'}
              </button>
            </div>
          </div>
          <div style={{ fontSize: '0.78rem', color: brand.charcoal, marginTop: '0.6rem' }}>
            Will be filed as <strong>{periodLabel}</strong>.
          </div>
          {error && (
            <div style={{ color: brand.red, fontSize: '0.85rem', marginTop: '0.6rem' }}>
              <i className="bi bi-exclamation-triangle-fill" style={{ marginRight: '0.4rem' }} />{error}
            </div>
          )}
          {notice && (
            <div style={{ color: brand.green, fontSize: '0.85rem', marginTop: '0.6rem' }}>
              <i className="bi bi-check-circle-fill" style={{ marginRight: '0.4rem' }} />{notice}
            </div>
          )}
        </form>
      </div>

      <div style={{ background: 'white', borderRadius: 12, boxShadow: '0 2px 12px rgba(10,41,71,0.1)', overflow: 'hidden' }}>
        <div style={{ background: brand.deepBlue, borderBottom: `3px solid ${brand.goldenrod}`, padding: '0.875rem 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <i className="bi bi-archive-fill" style={{ color: brand.goldenrod }} />
          <h5 style={{ color: 'white', fontWeight: 700, fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '1px', margin: 0 }}>
            Issues
          </h5>
          <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: '0.75rem', marginLeft: 'auto' }}>
            {bulletins.length} {bulletins.length === 1 ? 'issue' : 'issues'}
          </span>
        </div>
        <div style={{ padding: isMobile ? '0.5rem' : '0.75rem 1.25rem' }}>
          {isLoading ? (
            <div style={{ textAlign: 'center', padding: '2rem' }}>
              <div className="spinner-border" style={{ color: brand.tealBlue }} />
            </div>
          ) : bulletins.length === 0 ? (
            <p style={{ color: brand.charcoal, textAlign: 'center', padding: '1.5rem 0', margin: 0 }}>
              No issues uploaded yet.
            </p>
          ) : (
            <div className="table-responsive">
              <table className="table table-sm align-middle" style={{ fontSize: '0.86rem', marginBottom: 0 }}>
                <thead>
                  <tr style={{ color: brand.charcoal, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    <th>Period</th>
                    <th>Title</th>
                    <th>File</th>
                    <th>Uploaded by</th>
                    <th>Status</th>
                    <th className="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bulletins.map(b => (
                    <tr key={b.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {b.period_label || b.sort_date}
                        {b.id === currentId && (
                          <span style={{
                            marginLeft: '0.4rem', background: brand.goldenrod, color: 'white',
                            borderRadius: 4, padding: '0.05rem 0.35rem', fontSize: '0.65rem',
                            fontWeight: 700, textTransform: 'uppercase',
                          }}>
                            On the hub
                          </span>
                        )}
                      </td>
                      <td>{b.title}</td>
                      <td style={{ color: brand.charcoal, fontSize: '0.8rem' }}>
                        <i className={`bi bi-${(b.content_type || '').includes('pdf') ? 'file-earmark-pdf' : 'filetype-html'}`}
                           style={{ marginRight: '0.35rem' }} />
                        {b.filename || '—'}
                      </td>
                      <td style={{ color: brand.charcoal, fontSize: '0.8rem' }}>{b.uploaded_by || '—'}</td>
                      <td>
                        <span style={{
                          color: b.published ? brand.green : brand.charcoal,
                          fontWeight: 600, fontSize: '0.8rem',
                        }}>
                          {b.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <a
                          href={`/compliance/quick-hitter/view/${b.id}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-sm btn-outline-primary"
                          style={{ marginRight: '0.35rem' }}
                        >
                          Preview
                        </a>
                        <button
                          type="button"
                          onClick={() => togglePublished(b)}
                          className="btn btn-sm btn-outline-secondary"
                          style={{ marginRight: '0.35rem' }}
                        >
                          {b.published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          type="button"
                          onClick={() => onDelete(b)}
                          className="btn btn-sm btn-outline-danger"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
